'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState, } from 'react';
import { useLocalParticipant, useRemoteParticipants, useRoomContext, useTranscriptions, } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
const EMPTY = { transcriptLines: [], callEvents: [] };
const CallHistoryContext = createContext(EMPTY);
/** Transcript + event history for the active call. Reads from a shared store
 *  owned by `CallHistoryProvider`, so the data survives when a tab-switch
 *  unmounts the panels that render it. */
export const useCallHistory = () => useContext(CallHistoryContext);
/**
 * Owns the per-run transcript cache and call-event log. Mounts inside
 * `WebCallRoom` so it stays alive for the lifetime of the room — swapping
 * right-panel tabs no longer destroys the running state.
 *
 * `sessionKey` seeds the reset. A new key clears both caches so a second
 * test call does not inherit the first one's history. A `null` key (the call
 * ended) keeps them, so the finished call stays open to inspect.
 */
export function CallHistoryProvider({ sessionKey, cap = 500, children, }) {
    const transcriptLines = useTranscriptHistory(sessionKey);
    const callEvents = useEventHistory(sessionKey, cap);
    const value = useMemo(() => ({ transcriptLines, callEvents }), [transcriptLines, callEvents]);
    return (_jsx(CallHistoryContext.Provider, { value: value, children: children }));
}
function useTranscriptHistory(sessionKey) {
    const transcriptions = useTranscriptions();
    const { localParticipant } = useLocalParticipant();
    const remoteParticipants = useRemoteParticipants();
    const localIdentity = localParticipant.identity;
    // Staff identities are remembered for the whole call. The staff leg leaves
    // the room when it hangs up, and its lines must keep their label after that.
    const [staffIdentities, setStaffIdentities] = useState(() => new Set());
    useEffect(() => {
        const joined = remoteParticipants.filter(p => {
            if (!p.metadata || staffIdentities.has(p.identity))
                return false;
            try {
                return JSON.parse(p.metadata)?.role === 'staff_member';
            }
            catch {
                return false;
            }
        });
        if (joined.length === 0)
            return;
        setStaffIdentities(prev => {
            const next = new Set(prev);
            for (const p of joined)
                next.add(p.identity);
            return next;
        });
    }, [remoteParticipants, staffIdentities]);
    const [lines, setLines] = useState([]);
    useEffect(() => {
        if (sessionKey) {
            setLines([]);
            setStaffIdentities(new Set());
        }
    }, [sessionKey]);
    // The agent-side forwarder uses is_delta_stream=False for user transcripts,
    // so every interim ("hello", "hello hell", ...) arrives as a separate
    // TextStreamData entry with a different streamInfo id. Coalesce all
    // non-final user streams for a participant into a single pending slot;
    // the final stream (lk.transcription_final=true) commits as its own row
    // and clears the slot so the next utterance opens a fresh one. Agent
    // transcripts come from a delta stream (one id per utterance), so keying
    // by streamInfo.id for the agent lane already does the right thing.
    useEffect(() => {
        if (transcriptions.length === 0)
            return;
        setLines(prev => {
            const seen = new Map(prev.map(line => [line.key, line]));
            const pendingUserKeys = new Map();
            for (const [key, line] of seen) {
                if (line.role === 'you' && key.endsWith(':pending')) {
                    pendingUserKeys.set(key.split(':pending')[0], key);
                }
            }
            transcriptions.forEach(entry => {
                const identity = entry.participantInfo.identity;
                const role = identity === localIdentity
                    ? 'you'
                    : staffIdentities.has(identity)
                        ? 'staff'
                        : 'agent';
                const streamInfo = entry.streamInfo;
                const isFinal = streamInfo?.attributes?.['lk.transcription_final'] === 'true';
                if (role === 'agent') {
                    const key = streamInfo?.id ?? `${identity}:agent-latest`;
                    seen.delete(key);
                    seen.set(key, { key, role, text: entry.text });
                    return;
                }
                if (isFinal) {
                    const pending = pendingUserKeys.get(identity);
                    if (pending) {
                        seen.delete(pending);
                        pendingUserKeys.delete(identity);
                    }
                    const key = streamInfo?.id ?? `${identity}:${entry.text}`;
                    seen.delete(key);
                    seen.set(key, { key, role, text: entry.text });
                    return;
                }
                const pendingKey = `${identity}:pending`;
                pendingUserKeys.set(identity, pendingKey);
                seen.delete(pendingKey);
                seen.set(pendingKey, { key: pendingKey, role, text: entry.text });
            });
            return [...seen.values()];
        });
    }, [transcriptions, localIdentity, staffIdentities]);
    return lines;
}
function useEventHistory(sessionKey, cap) {
    const room = useRoomContext();
    const [entries, setEntries] = useState([]);
    useEffect(() => {
        if (sessionKey)
            setEntries([]);
    }, [sessionKey]);
    useEffect(() => {
        const decoder = new TextDecoder();
        const onData = (payload, _participant, _kind, topic) => {
            if (topic !== 'node-graph.event')
                return;
            let body;
            try {
                body = JSON.parse(decoder.decode(payload));
            }
            catch {
                return;
            }
            if (typeof body.eventType !== 'string')
                return;
            const event = {
                eventType: body.eventType,
                timestamp: body.timestamp ?? new Date().toISOString(),
                source: body.source ?? 'unknown',
                node: body.node ?? null,
                turnIndex: body.turnIndex ?? 0,
                successful: body.successful ?? true,
                parameters: body.parameters,
            };
            setEntries(prev => {
                const next = [...prev, event];
                return next.length > cap ? next.slice(-cap) : next;
            });
        };
        room.on(RoomEvent.DataReceived, onData);
        return () => {
            room.off(RoomEvent.DataReceived, onData);
        };
    }, [room, cap]);
    return entries;
}
//# sourceMappingURL=CallHistoryProvider.js.map